import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { Container, Loading, SimpleButton, ErrorMessage, SimpleTextField } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { useAuth } from '@/src/app/context/useAuth';
import Constants from '@/src/utils/Constants';
import type { DistrictBaseDTO } from './models/districtDTOs';
import DistrictsTable from './components/DistrictsTable';
import CreateEditDistrictModal from './components/CreateEditDistrictModal';
import DeleteDistrictModal from './components/DeleteDistrictModal';

const DistrictsPage: React.FC = () => {
    document.title = 'Districts';

    const { user } = useAuth();
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [districts, setDistricts] = useState<DistrictBaseDTO[]>([]);
    const [filteredDistricts, setFilteredDistricts] = useState<DistrictBaseDTO[]>([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedDistrict, setSelectedDistrict] = useState<DistrictBaseDTO | null>(null);
    const [searchString, setSearchString] = useState<string>('');

    const refreshDistricts = useCallback(async () => {
        setError('');
        setLoaded(false);
        try {
            const res = await apiRequest<DistrictBaseDTO[]>('GET', 'dbo/districts');
            setDistricts(res.data ?? []);
        } catch {
            setError('Failed to fetch districts');
        } finally {
            setLoaded(true);
        }
    }, []);

    const onSubmitCallback = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        setShowDeleteModal(false);
        setSelectedDistrict(null);
        refreshDistricts();
    };

    const handleDelete = async (id: number) => {
        setError('');
        try {
            await apiRequest('DELETE', `dbo/districts/${id}`);
            setShowDeleteModal(false);
            setSelectedDistrict(null);
            refreshDistricts();
        } catch {
            setError('Failed to delete district');
        }
    };

    useEffect(() => {
        refreshDistricts();
    }, [refreshDistricts]);

    useEffect(() => {
        const lowerSearch = searchString.toLowerCase();
        setFilteredDistricts(
            districts.filter(
                (district) =>
                    district.name.toLowerCase().includes(lowerSearch) ||
                    district.code.toLowerCase().includes(lowerSearch)
            )
        );
    }, [districts, searchString]);

    return (
        <Container>
            <h1 className="page-header">Districts</h1>
            <br />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <SimpleTextField
                    name="searchDistricts"
                    label="Search..."
                    variant="outlined"
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    sx={{ minWidth: 170, maxWidth: 300, mr: 2 }}
                />
                {user?.role === Constants.ROLES.SUPER_ADMIN && (
                    <SimpleButton
                        onClick={() => setShowCreateModal(true)}
                        variant="contained"
                        color="primary"
                        className="whitespace-nowrap"
                    >
                        Add District
                    </SimpleButton>
                )}
            </Box>
            <Loading visible={!loaded} message="Loading districts..." />
            <ErrorMessage error={error} />
            {loaded && (
                <DistrictsTable
                    data={filteredDistricts}
                    onEdit={(row) => {
                        setSelectedDistrict(row);
                        setShowEditModal(true);
                    }}
                    onDelete={(row) => {
                        setSelectedDistrict(row);
                        setShowDeleteModal(true);
                    }}
                />
            )}
            {showCreateModal && (
                <CreateEditDistrictModal
                    open={showCreateModal}
                    mode="create"
                    onClose={() => setShowCreateModal(false)}
                    onSubmitCallback={onSubmitCallback}
                />
            )}
            {showEditModal && selectedDistrict && (
                <CreateEditDistrictModal
                    open={showEditModal}
                    mode="edit"
                    district={selectedDistrict}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedDistrict(null);
                    }}
                    setError={setError}
                    onSubmitCallback={onSubmitCallback}
                    error={error}
                />
            )}
            {showDeleteModal && selectedDistrict && (
                <DeleteDistrictModal
                    open={showDeleteModal}
                    district={selectedDistrict}
                    onDelete={() => {
                        if (selectedDistrict?.id !== undefined && selectedDistrict?.id !== null) {
                            handleDelete(selectedDistrict.id);
                        } else {
                            setError('Invalid district id');
                        }
                    }}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setSelectedDistrict(null);
                    }}
                />
            )}
        </Container>
    );
};

export default DistrictsPage;
